import { refreshToken } from '@app/dashboard/services/outer_layout/login';
import {
  addToLocalStorage,
  handleAuthLogout,
  getAccessToken,
  getFromLocalStorage,
} from '@app/dashboard/utils/manageLocalStorage';
import { jwtDecode } from 'jwt-decode';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { useGlobalStore } from '@app/dashboard/store/global';

const { getState } = useGlobalStore;

const RETRIES = 2;
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let tokenRefreshQueue: Function[] = [];

const handleTokenRefreshQueue = (callback: Function) => {
  tokenRefreshQueue.push(callback);
};

const onTokenRefreshed = (newToken: string) => {
  tokenRefreshQueue.forEach((callback) => callback(newToken));
  tokenRefreshQueue = [];
};

export const saveTokenWithExpiry = async (accessToken: string) => {
  const jwtDecoded = jwtDecode<{ exp: number }>(accessToken);
  const expiryTimestamp = jwtDecoded?.exp && jwtDecoded.exp * 1000;
  addToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY, expiryTimestamp);
  addToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN, accessToken);
  try {
    const jwtDecoded = jwtDecode<{ exp: number }>(accessToken);
    if (!jwtDecoded?.exp) {
      throw new Error('Invalid token: missing token expiry time');
    }
    const expiryTimestamp = jwtDecoded.exp * 1000;
    addToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY, expiryTimestamp);
    addToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN, accessToken);
  } catch (error) {
    handleAuthLogout();
  }
};
export const refreshTokenWithRetry = async (delay = 1000): Promise<any> => {
  for (let attempt = 0; attempt < RETRIES; attempt++) {
    try {
      const response = await refreshToken();
      return response;
    } catch (error) {
      if (attempt < RETRIES - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay *= 2;
      } else {
        throw new Error(`Failed to refresh token after ${RETRIES} retries`);
      }
    }
  }
};
export const validateSession = async (bufferTime = 30 * 1000): Promise<string | null> => {
  const accessToken = getAccessToken();
  const tokenExp = getFromLocalStorage(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY)
    ? parseInt(getFromLocalStorage(LOCAL_STORAGE_KEYS.TOKEN_EXPIRY), 10)
    : null;
  const now = new Date().getTime();

  if (!accessToken || !tokenExp) {
    return null;
  };
  if (tokenExp - now <= bufferTime) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshTokenWithRetry()
        .then((response) => {
          const newAccessToken = response?.data?.access;
          saveTokenWithExpiry(newAccessToken);
          isRefreshing = false;
          onTokenRefreshed(newAccessToken);
          return newAccessToken;
        })
        .catch((error) => {
          isRefreshing = false;
          refreshPromise = null;
          getState().setIsAuthenticated(false);
          handleAuthLogout();
        });
    }

    return new Promise<string>((resolve, reject) => {
      handleTokenRefreshQueue((newToken: string) => {
        if (newToken) {
          resolve(newToken);
        } else {
          reject('Failed to refresh token');
        }
      });
    });
  }

  return accessToken;
};
